<template>
  <div class="text-center">
    <v-dialog v-model="importBookingDialog.visibility" max-width="560" persistent scrollable>
      <v-card class="pa-0" flat>
        <!-- Header -->
        <v-toolbar dark color="rgb(47, 207, 156)" dense>
          <v-btn icon disabled></v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>Import Booking</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="SET_IMPORT_BOOKING_DIALOG_VISIBILITY(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6 px-4">
          <v-row no-gutters justify="center">
            <!-- File Input -->
            <v-col cols="12">
              <v-file-input
                color="grey"
                dense
                show-size
                label="Booking Record"
                v-model="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              ></v-file-input>
            </v-col>

            <!-- Optional profile override -->
            <v-col cols="12">
              <v-select
                color="grey"
                dense
                clearable
                label="Force Import Profile (optional)"
                hint="Leave empty: every row is routed by its own CS Center value. Selecting a profile forces all rows into that profile."
                persistent-hint
                v-model="booking_import_profile_id"
                :items="profiles"
                item-value="booking_import_profile_id"
                :item-text="profileText"
              ></v-select>
            </v-col>

            <!-- Import Summary -->
            <v-col cols="12" v-if="summary" class="mt-4">
              <v-alert dense text :type="hasWarning ? 'warning' : 'success'" class="text-left text-body-2 mb-0">
                <div v-if="summary.profile_name"><strong>Forced profile:</strong> {{ summary.profile_name }}</div>
                <div><strong>Total rows:</strong> {{ count(summary.total) }}</div>
                <div><strong>Inserted:</strong> {{ count(summary.inserted) }}</div>
                <div><strong>Updated:</strong> {{ count(summary.updated) }}</div>
                <div><strong>Skipped:</strong> {{ count(summary.skipped) }}</div>

                <div class="mt-2"><strong>Routed to queues</strong></div>
                <div v-if="routedEntries.length">
                  <v-chip v-for="entry in routedEntries" :key="entry.queue" x-small label class="mr-1 mt-1">{{ entry.queue }}: {{ entry.count }}</v-chip>
                </div>
                <div v-else>No routing breakdown returned.</div>

                <div v-if="hasWarning" class="mt-3">
                  <div><strong>Needs attention</strong></div>
                  <div v-if="fallbackRows > 0">{{ fallbackRows }} row(s) had no matching CS Center profile and were routed to the default profile.</div>
                  <div v-if="unknownCsCenters.length">Unrecognised CS Center value(s): {{ unknownCsCenters.join(', ') }}</div>
                  <div>Review these rows before the customers arrive, or add a matching booking profile.</div>
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <!-- Disabled while a POST is in flight: the backend upsert is last-commit-wins, so two
               overlapping imports of files sharing a booking_id would race for the database. -->
          <v-btn block depressed :disabled="importing" :loading="importing" @click="importRecord">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
export default {
  name: 'importBookingDialog',
  data() {
    return {
      file: null,
      booking_import_profile_id: null
    }
  },
  computed: {
    ...mapState(['importBookingDialog']),
    profiles() {
      return this.importBookingDialog.profiles
    },
    summary() {
      return this.importBookingDialog.last_summary
    },
    // { RA: 207, RC: 0, RM: 5 } -> sorted chips, dropping the queues nothing landed in.
    routedEntries() {
      const routed = (this.summary && this.summary.routed) || {}
      return Object.keys(routed)
        .filter(queue => Number(routed[queue]) > 0)
        .sort()
        .map(queue => ({ queue, count: routed[queue] }))
    },
    fallbackRows() {
      return (this.summary && this.summary.fallback_rows) || 0
    },
    unknownCsCenters() {
      // Already display-normalised by utils/importSummary.js, so this alert and the snackbar
      // cannot name the same value differently.
      return (this.summary && this.summary.unknown_cs_centers) || []
    },
    importing() {
      return this.importBookingDialog.importing
    },
    // Derived once in the store so this alert and the snackbar always agree.
    hasWarning() {
      return !!(this.summary && this.summary.has_warning)
    }
  },
  methods: {
    ...mapMutations(['SET_IMPORT_BOOKING_DIALOG_VISIBILITY', 'SET_SNACKBAR']),
    ...mapActions(['importBookingRecords', 'fetchBookingImportProfiles']),
    profileText(item) {
      return item.service_type_prefix ? `${item.name} (${item.service_type_prefix})` : item.name
    },
    count(value) {
      return value == null ? '-' : value
    },
    importRecord() {
      if (this.file) {
        const formData = new FormData()
        formData.append('csv', this.file)
        if (this.booking_import_profile_id) {
          formData.append('booking_import_profile_id', this.booking_import_profile_id)
        }
        this.importBookingRecords({
          params: formData,
          headers: {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        })
      } else {
        this.SET_SNACKBAR({ status: true, color: 'error', text: 'Please select the file that you want to import.', icon: 'close', timeout: '2000' })
      }
    }
  },
  watch: {
    'importBookingDialog.visibility'() {
      if (this.importBookingDialog.visibility) {
        this.fetchBookingImportProfiles()
      } else {
        // Reset the dialog's OWN inputs. The summary panel is cleared by the visibility mutation
        // itself (open and close both end the session), so it is not reset from here — two
        // mechanisms for one promise is how the panel came to survive a close in the first place.
        this.file = null
        this.booking_import_profile_id = null
      }
    }
  }
}
</script>
